import type { FC } from 'react';
import type { ExtractionCheck } from '../../api/aiExtraction';
import { ClearableInput, FieldError } from '../base/ClearableInput';
import { ocrErrors, type OcrRole } from './ocrValidation';

export interface OcrData {
  hoTen: string;
  soCccd: string;
  ngaySinh: string;
  gioiTinh: string;
  diaChi: string;
  ngayCap: string;
  tenHtx?: string;
  maHtx?: string;
  vaiTroDaiDien?: string;
}

type Role = OcrRole;

interface OcrReviewFormProps {
  data: OcrData;
  role: Role;
  onChange: (data: OcrData) => void;
  /** AI is still reading one of the CCCD photos. */
  reading?: boolean;
  /** Checks on what was read (MRZ check digits, number vs birth year, front vs back). */
  checks?: ExtractionCheck[];
  /** Why there is nothing (or only sample data) to show — reading failed, or no AI on the server. */
  notice?: string;
  /** Show "required" errors on empty fields too (after the user tried to continue). */
  showAllErrors?: boolean;
}

const OcrReviewForm: FC<OcrReviewFormProps> = ({ data, role, onChange, reading, checks, notice, showAllErrors }) => {
  const set = (field: keyof OcrData, value: string) => onChange({ ...data, [field]: value });
  const errors = ocrErrors(data, role);
  // A value AI filled in wrong shows red at once; an empty field only once the
  // user has tried to continue.
  const shown = (key: keyof OcrData) => ((data[key] ?? '').trim() || showAllErrors ? errors[key] : undefined);

  const text: Array<{ key: keyof OcrData; label: string; placeholder: string; inputMode?: 'numeric' }> = [
    { key: 'hoTen', label: 'Họ và tên', placeholder: 'Nguyễn Văn A' },
    { key: 'soCccd', label: 'Số CCCD', placeholder: '12 chữ số', inputMode: 'numeric' },
    { key: 'ngaySinh', label: 'Ngày sinh', placeholder: 'dd/mm/yyyy' },
  ];
  const tail: Array<{ key: keyof OcrData; label: string; placeholder: string }> = [
    { key: 'diaChi', label: 'Địa chỉ thường trú', placeholder: 'Ấp/xã, huyện, tỉnh' },
    { key: 'ngayCap', label: 'Ngày cấp', placeholder: 'dd/mm/yyyy' },
  ];
  const htx: Array<{ key: keyof OcrData; label: string; placeholder: string }> = [
    { key: 'tenHtx', label: 'Tên HTX', placeholder: 'Tên hợp tác xã' },
    { key: 'maHtx', label: 'Mã HTX', placeholder: 'HTX-...' },
    { key: 'vaiTroDaiDien', label: 'Vai trò người đại diện', placeholder: 'VD: Giám đốc' },
  ];

  const input = ({ key, label, placeholder, inputMode }: { key: keyof OcrData; label: string; placeholder: string; inputMode?: 'numeric' }) => (
    <div key={key}>
      <label className="mb-1 block text-xs font-medium text-gray-600">
        {label}
        <span className="text-red-500"> *</span>
      </label>
      <ClearableInput
        value={data[key] ?? ''}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-label={label}
        maxLength={key === 'soCccd' ? 12 : undefined}
        onValueChange={(v) => set(key, key === 'soCccd' ? v.replace(/\D/g, '').slice(0, 12) : v)}
        error={shown(key)}
      />
    </div>
  );

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-agrida-50">
          <i className="ri-scan-2-line text-sm text-agrida-600" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900">Thông tin từ OCR</p>
          <p className="text-[11px] text-gray-500">{reading ? 'AI đang đọc ảnh CCCD…' : 'AI đọc từ ảnh CCCD — ô AI không đọc được để trống cho bạn nhập'}</p>
        </div>
      </div>

      {reading && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-sky-100 bg-sky-50 p-3 text-[11px] text-sky-700 animate-pulse">
          <i className="ri-sparkling-2-line" /> AI đang đọc ảnh CCCD và sẽ tự điền vào các ô bên dưới — vài giây đến nửa phút.
        </div>
      )}
      {notice && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
          <i className="ri-error-warning-line mr-1" />
          {notice}
        </div>
      )}
      {checks && checks.length > 0 && (
        <ul className="mb-3 space-y-1 rounded-xl border border-gray-100 bg-white p-3">
          {checks.map((c) => (
            <li key={c.code + c.label} className={`flex items-start gap-1.5 text-[11px] ${c.ok ? 'text-agrida-700' : 'text-amber-700'}`}>
              <i className={`mt-px ${c.ok ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`} />
              <span>{c.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3">
        {text.map(input)}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Giới tính<span className="text-red-500"> *</span>
          </label>
          <div className="flex gap-2">
            {['Nam', 'Nữ'].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => set('gioiTinh', data.gioiTinh === g ? '' : g)}
                className={`h-10 flex-1 rounded-lg border text-sm font-medium ${
                  data.gioiTinh === g ? 'border-agrida-500 bg-agrida-50 text-agrida-800' : shown('gioiTinh') ? 'border-red-400 bg-red-50/40 text-gray-700' : 'border-gray-300 text-gray-700'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <FieldError message={shown('gioiTinh')} />
        </div>
        {tail.map(input)}
        {role === 'Hợp tác xã' && <div className="space-y-3 rounded-xl border border-sky-100 bg-sky-50/50 p-3">{htx.map(input)}</div>}
      </div>

      <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 p-3">
        <p className="text-[11px] text-amber-700">
          <i className="ri-information-line mr-1" />
          Tất cả các ô đều bắt buộc. Ô viền đỏ là sai định dạng hoặc chưa nhập — bấm ✕ để xóa và nhập lại cho đúng trước khi tiếp tục.
        </p>
      </div>
    </div>
  );
};

export default OcrReviewForm;
